import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { AuthorizationService } from '@bo-client/core/authorization/authorization.service';
import { HttpHapService } from '@bo-client/core/http/http-hap.service';
import { DataShareService } from '@bo-client/shared/services/data-share.service';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { ErrorsFormsService } from '@bo-client/shared/form-errors/errors-forms.service';
import { GridRegistrationRequestService } from '@bo-client/activation-management/grid-registration/grid-registration-request.service';
import { CreateGridRegistrationFormsService } from '@bo-client/activation-management/grid-registration/create-grid-registration-forms.service';
import {
  ChangeMeteringConceptForm,
  GridRegistrationModel,
  MeterForm
} from '@bo-client/activation-management/grid-registration/model/grid-registration.model';
import { GridRegistrationFormBase } from '@bo-client/activation-management/grid-registration/grid-registration-form.base';
import { CreateFormsService } from '@bo-client/activation-management/shared/forms/create-forms.service';
import {
  GridRegistrationStatus,
  GridRegistrationStatusEnum,
  GridRegistrationTypeEnum
} from '../../../grid-registration/model/grid-registration.enum';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import {
  combineLatestWith,
  distinctUntilChanged,
  filter,
  map,
  shareReplay,
  startWith,
  tap
} from 'rxjs/operators';
import { CompanyLocationDto } from '@bo-client/shared/models/companyLocations';
import { AreaTypeEnum, CommonStatusEnum } from '../../../shared/models/commissioningModel';
import Utils from '@bo-client/shared/utils';
import { ValidatorsService } from '@bo-client/shared/forms/validators.service';
import { ConnectionObjectFormV2 } from '@bo-client/shared/models/connectionObject';
import { FieldConfigEnum } from '@bo-client/shared/constants/enums';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UploadForm } from '@bo-client/shared/models/documentDto';
import { UiAdaptionsModel } from '@bo-client/tenant-management/models/ui-adaptions.model';

@Component({
  selector: '' + 'app-grid-registration-change-metering-concept',
  templateUrl: './grid-registration-change-metering-concept.component.html',
  styleUrls: ['./grid-registration-change-metering-concept.component.scss']
})
export class GridRegistrationChangeMeteringConceptComponent
  extends GridRegistrationFormBase
  implements OnInit
{
  private destroyRef = inject(DestroyRef);
  private validatorsService = inject(ValidatorsService);
  private modelState$ = new BehaviorSubject<CommonStatusEnum | null>(null);

  originalModel: GridRegistrationModel;
  connectionObjectData$: Observable<ConnectionObjectFormV2['value']>;

  commissioningDateConfig$: Observable<FieldConfigEnum>;
  newInstalledPowerConfig$: Observable<FieldConfigEnum>;

  constructor(
    protected authorizationService: AuthorizationService,
    protected httpHapService: HttpHapService,
    protected dataShareService: DataShareService,
    protected route: ActivatedRoute,
    protected router: Router,
    protected toastService: ToastrService,
    protected errorsFormsService: ErrorsFormsService,
    private fb: FormBuilder,
    private createFormService: CreateFormsService,
    protected gridRegistrationService: GridRegistrationRequestService,
    protected createFormsGridRegistrationService: CreateGridRegistrationFormsService,
    private CustomValidators: ValidatorsService
  ) {
    super(authorizationService, dataShareService);

    this.commissioningDateConfig$ = this.dataShareService.getSingleTenantConfig$<FieldConfigEnum>(
      'gridRegistration.changeMeteringConcept.commissioningDateField'
    );
    this.newInstalledPowerConfig$ = this.dataShareService.getSingleTenantConfig$<FieldConfigEnum>(
      'gridRegistration.changeMeteringConcept.newInstalledPowerField'
    );
    this.gridRegistrationForm = this.fb.group({
      acceptedConditions1: [false, this.CustomValidators.checkboxRequiredValidator()],
      assignedTransformerStation: [''],
      comment: [''],
      commissioningDate: [null as Date | string],
      companyLocation: this.fb.group({ id: [null as number] }),
      connectionObject: this.createFormService.connectionObjectFormWithValidatorV2({}),
      connectionObjectAdditionalInfo: this.createFormService.connectionObjectAdditionalInfoForm({}),
      customer: this.createFormService.createContactDataFormV2({}, true),
      externalId: [''],
      installerFeedbackComment: [''],
      formVersion: fb.control<'v1' | 'v2'>('v2'),
      gridConnectionPointId: [''],
      hasDifferentInvoiceContact: [false],
      invoiceContact: this.createFormService.createContactDataFormV2({}, false),
      longDescription: [''],
      meteringConcept: this.fb.group({ id: [null as number, Validators.required] }),
      meterNumber: [''],
      meterOne: this.createMeterForm(),
      meterTwo: this.createMeterForm(),
      meterThree: this.createMeterForm(),
      newInstalledPower: [null as number],
      oldMeteringConcept: this.fb.group({ id: [null as number, Validators.required] }),
      shortDescription: [''],
      state: [''] as [GridRegistrationStatus | CommonStatusEnum | ''],
      uploads: this.fb.array<UploadForm>([]),
      deviceType: [null as number]
    });
    this.initFieldConfigListeners();
    this.initConnectionObjectData$();
  }

  get gridRegistrationType(): GridRegistrationTypeEnum {
    return this.originalModel?.gridRegistrationType;
  }

  get documentsUploadInfoUIAdaptionName() {
    return 'gridRegistration.' + this.gridRegistrationType.valueOf() + '.documentsUploadInfo';
  }

  gridRegistrationForm: ChangeMeteringConceptForm;
  customConditionsCheckbox = new FormControl(false);

  canUpdate$: Observable<boolean>;
  disableAll$: Observable<boolean>;

  readonly GridRegistrationTypeEnum = GridRegistrationTypeEnum;

  selectedCompanyLocation$: Observable<CompanyLocationDto>;

  protected readonly AreaTypeEnum = AreaTypeEnum;

  getFormGroup() {
    return this.gridRegistrationForm as FormGroup;
  }

  ngOnInit(): void {
    combineLatest([this.dataShareService.uiAdaptions$, this.modelState$])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([adaptions]) => {
        const checkboxAdaption = adaptions.find(
          (adaption) => adaption.anchor === 'gridRegistration.ChangeMeteringConcept.conditionsCheckbox'
        );
        this.updateCustomConditionsCheckboxValidation(checkboxAdaption);
      });

    this.canUpdate$ = of(true);

    this.disableAll$ = this.gridRegistrationForm.controls.state.valueChanges.pipe(
      takeUntilDestroyed(this.destroyRef),
      startWith(this.gridRegistrationForm.controls.state.value),
      combineLatestWith(
        this.authorizationService.hasOneOfThisRoles$(['ROLE_INSTALLATEUR', 'ROLE_ADMIN_INSTALLATEUR']),
        this.authorizationService.hasRole$('ROLE_EDITOR_GRIDREGISTRATIONS'),
        this.authorizationService.hasRole$('ROLE_SUPERADMIN')
      ),
      map(([stateValue, isInstaller, isEditor, isSuperAdmin]) => {
        const canEditAsInstaller =
          isInstaller &&
          [GridRegistrationStatusEnum.Incomplete, GridRegistrationStatusEnum.FeedbackNeeded].includes(
            stateValue as CommonStatusEnum
          );
        const canEditAsEditor =
          isEditor &&
          [
            GridRegistrationStatusEnum.Requested,
            GridRegistrationStatusEnum.Approved,
            GridRegistrationStatusEnum.UnderReview
          ].includes(stateValue as CommonStatusEnum);
        const canEdit = canEditAsEditor || canEditAsInstaller || isSuperAdmin;

        // Keine Ahnung was das soll, ich lass es mal stehen
        if (!canEdit) {
          this.disableFields();
        } else {
          this.enableFields();
        }

        return !canEdit;
      })
    );

    this.selectedCompanyLocation$ =
      this.gridRegistrationForm.controls.connectionObject.controls.orgUnitDesc.valueChanges.pipe(
        startWith(this.gridRegistrationForm.controls.connectionObject.controls.orgUnitDesc.value),
        combineLatestWith(this.dataShareService.companyLocations$),
        filter(([orgUnitDesc, companyLocations]) => !!orgUnitDesc && !!companyLocations),
        map(([orgUnitDesc, companyLocations]) => {
          return companyLocations.find((x) => x.external_id === orgUnitDesc);
        }),
        // after we selected the companyLocation we want to patch the id into the form
        tap((val) =>
          this.gridRegistrationForm.patchValue({ companyLocation: { id: val.id } }, { emitEvent: false })
        )
      );
    this.selectedCompanyLocation$.subscribe();
  }

  getModelFromForm(): GridRegistrationModel {
    const response = this.gridRegistrationForm.value;
    const updatedModel = {
      ...this.originalModel,
      ...response
    };

    Utils.logDiff(this.originalModel, updatedModel);

    return updatedModel as GridRegistrationModel;
  }

  /**
   * Hier wird von der enthaltenden Page angefragt, ob das Formular valide ist
   * Wird z.B. vor Abschicken geprüft.
   */
  isValid() {
    this.gridRegistrationForm.markAllAsTouched();
    this.customConditionsCheckbox.markAllAsTouched();
    // valid ist nicht true, wenn die formGroup disabled ist, darum müssen wir dies hier extra abfragen.
    return (
      (this.gridRegistrationForm.valid || this.gridRegistrationForm.disabled) &&
      this.customConditionsCheckbox.valid
    );
  }

  /**
   * Dies wird bei der initialen Befüllung des Forms aufgerufen.
   * @param model
   */
  loadFormWithModel(model: GridRegistrationModel) {
    this.updateFormWithModelInternal(model);
  }

  /**
   * Und das hier z.B. nachdem etwas gespeichert wurde
   * @param model
   */
  updateFormWithModel(model: GridRegistrationModel) {
    this.updateFormWithModelInternal(model);
  }

  private updateFormWithModelInternal(model: GridRegistrationModel) {
    // we want to store the "old" model to patch it later
    this.originalModel = model;
    this.clearAllFormArrays();

    const uploads = this.gridRegistrationForm.controls.uploads;

    model.uploads.forEach((upload) => {
      const uploadFormGroup = this.createFormService.createUploadForm(upload);
      uploads.push(uploadFormGroup);
    });

    // Laden Sie jetzt das Modell in das Formular
    this.gridRegistrationForm.patchValue(model, { emitEvent: true });

    // Wir leiten uns die transiente prop her, ob ein abweichender Rechnungsempfänger vorhanden ist
    this.gridRegistrationForm.controls.hasDifferentInvoiceContact.patchValue(
      !!model.invoiceContact?.salutation
    );
    this.modelState$.next(model?.state as CommonStatusEnum);
  }

  private clearAllFormArrays() {
    const uploads = this.gridRegistrationForm.controls.uploads;
    const formArrays = [uploads];

    formArrays.forEach((formArray) => formArray.clear());
  }

  protected readonly gridRegistrationTypeEnum = GridRegistrationTypeEnum;

  resetDifferingInvoiceContactForm(checked: boolean) {
    if (!checked) {
      for (const control of Object.values(this.gridRegistrationForm.controls.invoiceContact.controls)) {
        control.clearValidators();
        control.patchValue(null);
      }
    }
  }

  loadErrorsList() {
    // empty
  }

  private createMeterForm(): MeterForm {
    return this.fb.group({
      meterNo: [''],
      obisCode: [''],
      value: ['']
    });
  }

  private initConnectionObjectData$() {
    this.connectionObjectData$ = this.gridRegistrationForm.controls.connectionObject.valueChanges.pipe(
      takeUntilDestroyed(this.destroyRef),
      startWith(this.gridRegistrationForm.controls.connectionObject.value),
      shareReplay(1)
    );
  }

  private initFieldConfigListeners() {
    this.commissioningDateConfig$
      .pipe(takeUntilDestroyed(this.destroyRef), distinctUntilChanged())
      .subscribe({
        next: (value) => {
          switch (value) {
            case FieldConfigEnum.REQUIRED: {
              this.gridRegistrationForm.controls.commissioningDate.setValidators(Validators.required);
              break;
            }
            default: {
              this.gridRegistrationForm.controls.commissioningDate.clearValidators();
              break;
            }
          }
          this.gridRegistrationForm.controls.commissioningDate.updateValueAndValidity();
        }
      });
    this.newInstalledPowerConfig$
      .pipe(takeUntilDestroyed(this.destroyRef), distinctUntilChanged())
      .subscribe({
        next: (value) => {
          switch (value) {
            case FieldConfigEnum.REQUIRED: {
              this.gridRegistrationForm.controls.newInstalledPower.setValidators(Validators.required);
              break;
            }
            default: {
              this.gridRegistrationForm.controls.newInstalledPower.clearValidators();
              break;
            }
          }
          this.gridRegistrationForm.controls.newInstalledPower.updateValueAndValidity();
        }
      });
  }

  private updateCustomConditionsCheckboxValidation(checkboxAdaption: UiAdaptionsModel | undefined): void {
    if (
      !this.customConditionsCheckbox.disabled &&
      checkboxAdaption &&
      (checkboxAdaption.visibility === 'show' || checkboxAdaption.visibility === 'true')
    ) {
      this.customConditionsCheckbox.setValidators(this.validatorsService.checkboxRequiredValidator());
    } else {
      this.customConditionsCheckbox.clearValidators();
    }
    if (
      this.originalModel?.state !== CommonStatusEnum.Incomplete &&
      this.originalModel?.state !== CommonStatusEnum.FeedbackNeeded
    ) {
      this.customConditionsCheckbox.clearValidators();
      this.customConditionsCheckbox.setValue(true);
    }
    this.customConditionsCheckbox.updateValueAndValidity();
  }

  protected readonly FieldConfigEnum = FieldConfigEnum;
}
